import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { FileText, Brain, Eye, TrendingUp, Share2, Heart, Mail, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import AdminLayout from "@/components/admin/AdminLayout";

type TimePeriod = 'daily' | 'weekly' | 'monthly' | 'all' | 'custom';

const AdminDashboard = () => {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    totalQuizzes: 0,
    totalShares: 0,
    totalViews: 0,
    totalLikes: 0,
    totalSubscribers: 0,
  });

  const getDateRange = (): { startDate: string | null; endDate: string | null } => {
    const now = new Date();
    let startDate: Date | null = null;

    switch (timePeriod) {
      case 'daily':
        startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case 'weekly':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        if (customDateRange.from) {
          return {
            startDate: customDateRange.from.toISOString(),
            endDate: customDateRange.to ? customDateRange.to.toISOString() : new Date().toISOString(),
          };
        }
        return { startDate: null, endDate: null };
      case 'all':
      default:
        return { startDate: null, endDate: null };
    }

    return {
      startDate: startDate ? startDate.toISOString() : null,
      endDate: now.toISOString(),
    };
  };

  const getTimePeriodLabel = () => {
    switch (timePeriod) {
      case 'daily':
        return 'Last 24 Hours';
      case 'weekly':
        return 'Last 7 Days';
      case 'monthly':
        return 'Last 30 Days';
      case 'custom':
        if (customDateRange.from && customDateRange.to) {
          return `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`;
        }
        if (customDateRange.from) {
          return `From ${format(customDateRange.from, 'MMM d, yyyy')}`;
        }
        return 'Custom Range';
      case 'all':
      default:
        return 'All Time';
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { startDate, endDate } = getDateRange();

        let postsQuery = supabase.from("blog_posts").select("*", { count: "exact", head: true });
        let publishedQuery = supabase.from("blog_posts").select("*", { count: "exact", head: true }).eq("status", "published");
        let draftQuery = supabase.from("blog_posts").select("*", { count: "exact", head: true }).eq("status", "draft");
        let quizzesQuery = supabase.from("quizzes").select("*", { count: "exact", head: true });
        let sharesQuery = supabase.from("article_shares").select("*", { count: "exact", head: true });
        let viewsQuery = supabase.from("article_views").select("*", { count: "exact", head: true });
        let likesQuery = supabase.from("blog_post_likes").select("*", { count: "exact", head: true });
        let subscribersQuery = supabase.from("newsletter_subscriptions").select("*", { count: "exact", head: true }).eq("is_active", true);

        if (startDate && endDate) {
          postsQuery = postsQuery.gte("created_at", startDate).lte("created_at", endDate);
          publishedQuery = publishedQuery.gte("created_at", startDate).lte("created_at", endDate);
          draftQuery = draftQuery.gte("created_at", startDate).lte("created_at", endDate);
          quizzesQuery = quizzesQuery.gte("created_at", startDate).lte("created_at", endDate);
          sharesQuery = sharesQuery.gte("shared_at", startDate).lte("shared_at", endDate);
          viewsQuery = viewsQuery.gte("viewed_at", startDate).lte("viewed_at", endDate);
          likesQuery = likesQuery.gte("created_at", startDate).lte("created_at", endDate);
          subscribersQuery = subscribersQuery.gte("subscribed_at", startDate).lte("subscribed_at", endDate);
        }

        const [
          { count: totalPosts },
          { count: publishedPosts },
          { count: draftPosts },
          { count: totalQuizzes },
          { count: totalShares },
          { count: totalViews },
          { count: totalLikes },
          { count: totalSubscribers },
        ] = await Promise.all([
          postsQuery,
          publishedQuery,
          draftQuery,
          quizzesQuery,
          sharesQuery,
          viewsQuery,
          likesQuery,
          subscribersQuery,
        ]);

        setStats({
          totalPosts: totalPosts || 0,
          publishedPosts: publishedPosts || 0,
          draftPosts: draftPosts || 0,
          totalQuizzes: totalQuizzes || 0,
          totalShares: totalShares || 0,
          totalViews: totalViews || 0,
          totalLikes: totalLikes || 0,
          totalSubscribers: totalSubscribers || 0,
        });
      } catch (error) {
        // Silent error handling
      }
    };

    fetchStats();
  }, [timePeriod, customDateRange]);

  const periodLabel = getTimePeriodLabel();
  
  const statCards = [
    {
      title: `Total Posts${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.totalPosts,
      icon: FileText,
      color: "text-blue-500",
    },
    {
      title: `Published Posts${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.publishedPosts,
      icon: Eye,
      color: "text-green-500",
    },
    {
      title: `Draft Posts${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.draftPosts,
      icon: TrendingUp,
      color: "text-yellow-500",
    },
    {
      title: `Total Views${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.totalViews,
      icon: Eye,
      color: "text-purple-500",
    },
    {
      title: `Total Shares${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.totalShares,
      icon: Share2,
      color: "text-pink-500",
    },
    {
      title: `Total Likes${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.totalLikes,
      icon: Heart,
      color: "text-red-500",
    },
    {
      title: `Newsletter Subscribers${timePeriod !== 'all' ? ` (${periodLabel})` : ''}`,
      value: stats.totalSubscribers,
      icon: Mail,
      color: "text-indigo-500",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Welcome to your admin dashboard
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Tabs value={timePeriod} onValueChange={(value) => setTimePeriod(value as TimePeriod)}>
            <TabsList>
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="all">All Time</TabsTrigger>
              <TabsTrigger value="custom">Custom</TabsTrigger>
            </TabsList>
          </Tabs>

          {timePeriod === 'custom' && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !customDateRange.from && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {customDateRange.from ? (
                    customDateRange.to ? (
                      <>
                        {format(customDateRange.from, "MMM d, yyyy")} - {format(customDateRange.to, "MMM d, yyyy")}
                      </>
                    ) : (
                      format(customDateRange.from, "MMM d, yyyy")
                    )
                  ) : (
                    <span>Pick a date range</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={{ from: customDateRange.from, to: customDateRange.to }}
                  onSelect={(range) => setCustomDateRange({ from: range?.from, to: range?.to })}
                  numberOfMonths={2}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
